import { useTranslation } from 'react-i18next';
import { useGetModuleQuery } from '@platform/state/api';
import { useLabel } from '@platform/hooks/useLabel';
import { Icon } from '@platform/ui';
import { relativeTime } from '@platform/core/lib/format';
import { routes } from '@platform/core/lib/routes';
import { statusColor } from '@platform/core/lib/statusColors';
import { WorkspaceLink } from '@platform/navigation';
import { useAppSelector } from '@platform/state/hooks';
import { selectLang } from '@platform/state/selectors';
import { displayNumber } from './displayNumber';
import type { IRecordRowProps } from './IRecordRowProps';

/**
 * Рядок таблиці «Нещодавні» / «Обране» (прототип v4): номер IBM Plex Mono 12px (голий, як у прототипі; повне позначення — у підказці), назва 500 з «…» (посилання на запис), модуль 12px, статус —
 * крапка 7px і підпис набору статусів типу (з визначення модуля), виконавець 12px, «коли» праворуч; на «Обраному» — ★ «Прибрати з обраного».
 * Статтю бази знань відкриває на «Читанні», решту записів — на «Загальному» (прототип v4 rowVM: tab mm.key === 'kb' ? 'read' : 'general').
 */
export function RecordRow({ item, moduleName, onRemove }: IRecordRowProps) {
  const { t } = useTranslation();
  const label = useLabel();
  const lang = useAppSelector(selectLang);
  const { data: module } = useGetModuleQuery({ key: item.module, workspaceId: item.workspaceId });
  const statuses = module?.recordTypes.find((r) => r.key === item.recordType)?.statuses;
  const status = statuses?.list.find((s) => s.value === item.status);
  return (
    <div role="row" className="rp-row">
      <span role="cell" className="rp-code" title={item.display}>{displayNumber(item.display)}</span>
      <span role="cell" className="rp-title"><WorkspaceLink ws={item.workspaceId} to={routes.record(item.module, item.recordType, item.recordId, item.module === 'kb' ? 'read' : undefined)}>{item.title || item.display}</WorkspaceLink></span>
      <span role="cell" className="rp-muted">{moduleName}</span>
      <span role="cell" className="rp-status">
        {item.status && <span className="rp-dot" style={{ background: statuses ? statusColor(statuses, item.status, item.recordType) : 'var(--line2)' }} aria-hidden="true" />}
        {label(status?.label) || item.status}
      </span>
      <span role="cell" className="rp-muted">{item.assignee ?? '—'}</span>
      <span role="cell" className="rp-muted rp-right">{relativeTime(item.at, lang)}</span>
      {onRemove && (
        <span role="cell">
          <button type="button" className="rp-star" title={t('recordsPages.removeFavourite')} aria-label={t('recordsPages.removeFavourite')} onClick={() => onRemove(item.recordId)}>
            <Icon name="star" size={14} filled />
          </button>
        </span>
      )}
    </div>
  );
}
